import { describeCue } from '../av/cues';
import { extractEvents } from '../av/events';
import { mappingTriggers } from '../av/reactions';
import { TICKS_PER_BEAT, formatPosition } from '../av/time';
import { TEMPLATES } from '../animation/procedural/spec';
import * as av from '../domain/avOps';
import { OpError } from '../domain/projectOps';
import { SCENE_REACTIONS, type AvMapping, type AvSession, type Project, type ReactionType, type SceneObject } from '../domain/schemas';
import type { Command } from './commands';
import { objectsNamed } from './motionCommands';
import { NOUNS } from './motionGrammar';

type SyncCommand = Extract<Command, { type: `sync.${string}` }>;

/** Explicit Sync-workspace context: the selected session and reaction, plus the canvas selection and timeline animation. */
export interface SyncPromptSelection {
  sessionId?: string | null;
  mappingId?: string | null;
  objectIds?: readonly string[];
  animationId?: string | null;
}

export interface SyncCommandResult {
  project: Project;
  message: string;
  select?: { sessionId?: string | null; mappingId?: string | null };
}

const SCENE = new Set<string>(SCENE_REACTIONS);

/** Kinds named in prompts ("the pulse") → reactions. */
const KIND_REACTIONS: Record<string, ReactionType[]> = {
  pulse: ['scale', 'opacity', 'tint'],
  scale: ['scale'],
  bounce: ['position'],
  flash: ['flash', 'background', 'tint'],
  glow: ['tint'],
  shake: ['shake'],
  spin: ['rotation'],
  rotation: ['rotation'],
  fade: ['opacity'],
  blink: ['visibility'],
  background: ['background'],
  pose: ['pose'],
};

/**
 * The session a command acts on: the selected one, else the only one. With none, commands
 * that add something create one for the active scene (said in the message); several
 * sessions without a selection is ambiguous and refused.
 */
function targetSession(project: Project, selection: SyncPromptSelection | undefined, create: boolean): { project: Project; session: AvSession; created: string | null } {
  const selected = selection?.sessionId ? project.avSessions.find((s) => s.id === selection.sessionId) : undefined;
  if (selected) return { project, session: selected, created: null };
  if (project.avSessions.length === 1) return { project, session: project.avSessions[0]!, created: null };
  if (project.avSessions.length > 1) throw new OpError(`This project has ${project.avSessions.length} sync sessions (${project.avSessions.map((s) => s.name).join(', ')}). Select one in the Sync tab first.`);
  if (!create) throw new OpError('There is no sync session yet. Say “synchronize this animation with the phonk track” first.');
  const animationId = selection?.animationId && project.animations.some((a) => a.id === selection.animationId && a.sceneId === project.activeSceneId) ? selection.animationId : undefined;
  const r = av.createSession(project, { animationId });
  const session = av.sessionOf(r.project, r.id);
  return { project: r.project, session, created: session.name };
}

function resolveObjects(project: Project, session: AvSession, target: Extract<SyncCommand, { type: 'sync.react' }>['target'], selection: SyncPromptSelection | undefined): SceneObject[] {
  const scene = project.scenes.find((s) => s.id === session.sceneId)!;
  const selected = scene.objects.filter((o) => selection?.objectIds?.includes(o.id));
  const characters = (list: SceneObject[]) => list.filter((o) => o.type === 'character');
  const named = (name: string) => {
    const found = objectsNamed(scene, name);
    if (found.length === 1) return found;
    if (found.length === 0) throw new OpError(`No object in “${scene.name}” is called “${name}”. Use its name from the layer list, or select it and say “the selected object”.`);
    throw new OpError(`“${name}” matches ${found.length} objects in “${scene.name}” (${found.slice(0, 5).map((o) => o.name).join(', ')}). Which one? Use its full name or select it.`);
  };
  switch (target.kind) {
    case 'scene':
      return [];
    case 'selected-name': {
      const wanted = new Set(objectsNamed(scene, target.name).map((o) => o.id));
      const both = selected.filter((o) => wanted.has(o.id));
      if (both.length) return both;
      if (selected.length === 1 && wanted.size === 0) return selected;
      return named(target.name);
    }
    case 'selected':
      if (!selected.length) throw new OpError(`No object of “${scene.name}” is selected. Select one in the Animation tab, or name it (“pulse the neon sign on every kick”).`);
      return selected;
    case 'selected-character':
      if (!characters(selected).length) throw new OpError('No character is selected.');
      return characters(selected);
    case 'inherit':
    case 'character': {
      // "inherit" (no explicit target) uses the selection, else the character.
      if (target.kind === 'inherit' && selected.length) return selected;
      if (characters(selected).length) return characters(selected);
      const all = characters(scene.objects);
      if (all.length === 1) return all;
      if (all.length === 0) throw new OpError(`There is no character in “${scene.name}”.`);
      throw new OpError(`“${scene.name}” has ${all.length} characters (${all.map((o) => o.name).join(', ')}). Which one? Select it or use its name.`);
    }
    case 'name':
      return named(target.name);
  }
}

/** The one reaction a prompt refers to ("the pulse", "the selected reaction", "the kick reaction"). */
function findMapping(session: AvSession, which: string, category: AvMapping['source']['categories'][number] | null, selection: SyncPromptSelection | undefined): AvMapping {
  const selected = session.mappings.find((m) => m.id === selection?.mappingId);
  if (which === 'selected') {
    if (!selected) throw new OpError('No reaction is selected. Select one in the Sync tab’s reaction list.');
    return selected;
  }
  const kind = which.replace(/^(?:the |selected )/, '').trim();
  const reactions = KIND_REACTIONS[kind];
  let list = session.mappings.filter((m) => (!reactions || reactions.includes(m.reaction)) && (!category || m.source.categories.includes(category)));
  if (!reactions && !['reaction', 'mapping', 'cue'].includes(kind)) throw new OpError(`I don’t know which reaction “${which}” is. Say the pulse, bounce, flash, shake, spin, fade or glow, or select one.`);
  if (selected && list.includes(selected)) list = [selected];
  if (list.length === 1) return list[0]!;
  const what = `${category ? `${av.CATEGORY_LABELS[category].toLowerCase()} ` : ''}${kind === 'mapping' ? 'reaction' : kind}`;
  if (list.length === 0) throw new OpError(`“${session.name}” has no ${what}.`);
  throw new OpError(`“${session.name}” has ${list.length} matching reactions (${list.map((m) => `“${m.name}”`).join(', ')}). Which one? Select it in the Sync tab.`);
}

export function applySyncCommand(project: Project, command: SyncCommand, selection: SyncPromptSelection | undefined): SyncCommandResult | null {
  switch (command.type) {
    case 'sync.play':
    case 'sync.pause':
      return null;
    case 'sync.create': {
      const scene = project.scenes.find((s) => s.id === project.activeSceneId)!;
      const animationId = selection?.animationId && project.animations.some((a) => a.id === selection.animationId && a.sceneId === scene.id) ? selection.animationId : (project.animations.find((a) => a.sceneId === scene.id)?.id ?? null);
      const existing = project.avSessions.find((s) => s.sceneId === scene.id && s.animationId === animationId && s.music.kind === 'song');
      const animation = project.animations.find((a) => a.id === animationId);
      if (existing) return { project, message: `“${existing.name}” already links “${scene.name}”${animation ? ` and “${animation.name}”` : ''} with the song; it is selected in the Sync tab.`, select: { sessionId: existing.id } };
      const r = av.createSession(project, { sceneId: scene.id, animationId });
      const s = av.sessionOf(r.project, r.id);
      const bars = Math.round(av.musicTicks(r.project, s) / (4 * TICKS_PER_BEAT));
      return {
        project: r.project,
        message: `Created sync session “${s.name}”: the song (${bars} bars at ${project.settings.bpm} BPM) with “${scene.name}”${animation ? ` and “${animation.name}” (${animation.fps} fps)` : ' (no animation)'} on one clock. Add reactions, then preview.`,
        select: { sessionId: r.id },
      };
    }
    case 'sync.react': {
      const t = targetSession(project, selection, true);
      const scenic = SCENE.has(command.reaction);
      const objects = scenic ? [] : resolveObjects(t.project, t.session, command.target, selection);
      if (!scenic && command.reaction === 'pose' && objects.some((o) => o.type !== 'character')) throw new OpError('Pose reactions need a character.');
      let next = t.project;
      let last = '';
      const names: string[] = [];
      for (const o of scenic ? [null] : objects) {
        const m = av.defaultMapping(command.reaction, command.categories, o?.id ?? null);
        m.source.every = command.every;
        if (command.direction) m.params.direction = command.direction;
        if (command.color) m.params.color = command.color;
        if (command.every > 1) m.name = `${m.name} (every ${command.every})`.slice(0, 60);
        next = av.addMapping(next, t.session.id, m);
        last = m.id;
        names.push(o?.name ?? 'the scene');
      }
      const session = av.sessionOf(next, t.session.id);
      const x = extractEvents(next, session);
      const added = session.mappings.find((m) => m.id === last)!;
      const n = mappingTriggers(added, x).length;
      const events = command.categories.map((c) => av.CATEGORY_LABELS[c].toLowerCase()).join(' + ');
      return {
        project: next,
        message: `${t.created ? `Created sync session “${t.created}”. ` : ''}Added ${av.REACTION_LABELS[command.reaction].toLowerCase()} → ${names.join(', ')} on ${command.every > 1 ? `every ${command.every}th ` : 'every '}${events} (${n} triggers in “${session.name}”). A reactive overlay: no keyframes are written.`,
        select: { sessionId: session.id, mappingId: last },
      };
    }
    case 'sync.cue': {
      const t = targetSession(project, selection, true);
      // With no explicit target, a character-only motion ("start the running …") means the character.
      const target = command.target.kind === 'inherit' && TEMPLATES[command.template].characterOnly ? { kind: 'character' as const } : command.target;
      const [o, ...more] = resolveObjects(t.project, t.session, target, selection);
      if (!o || more.length) throw new OpError('A motion cue needs exactly one object. Select one, or name it.');
      if (TEMPLATES[command.template].characterOnly && o.type !== 'character') throw new OpError(`${TEMPLATES[command.template].label} needs a character (“${o.name}” is a ${o.type}).`);
      const total = av.musicTicks(t.project, t.session);
      if (command.tick >= total) throw new OpError(`The music ends at ${formatPosition(total)}; ${formatPosition(command.tick)} is after it.`);
      const count = command.everyTicks ? Math.max(1, Math.min(64, Math.floor((total - 1 - command.tick) / command.everyTicks) + 1)) : 1;
      const r = av.addMotionCue(t.project, t.session.id, { template: command.template, objectId: o.id, tick: command.tick, everyTicks: command.everyTicks, count, durationBeats: null, params: {} });
      const cue = av.sessionOf(r.project, t.session.id).motionCues.find((c) => c.id === r.id)!;
      return {
        project: r.project,
        message: `${t.created ? `Created sync session “${t.created}”. ` : ''}Added a motion cue: ${o.name} — ${describeCue(cue)} (Phase 3C motion, played as an overlay; “Bake into keyframes” in the Sync tab makes it permanent).`,
        select: { sessionId: t.session.id },
      };
    }
    case 'sync.strength': {
      const t = targetSession(project, selection, false);
      const m = findMapping(t.session, command.which, null, selection);
      const next = av.scaleMappingStrength(project, t.session.id, m.id, command.factor);
      const after = av.sessionOf(next, t.session.id).mappings.find((x) => x.id === m.id)!;
      return { project: next, message: `“${m.name}” strength ${m.params.amount} → ${after.params.amount}${after.params.amount === m.params.amount ? ' (already at its limit)' : ''}.`, select: { sessionId: t.session.id, mappingId: m.id } };
    }
    case 'sync.remove': {
      const t = targetSession(project, selection, false);
      if (command.which === 'cue' || command.which === 'motion cue') {
        if (t.session.motionCues.length !== 1) throw new OpError(t.session.motionCues.length ? 'Which motion cue? Remove it from the Sync tab’s cue list.' : 'There are no motion cues.');
        return { project: av.removeMotionCue(project, t.session.id, t.session.motionCues[0]!.id), message: 'Removed the motion cue.' };
      }
      const m = findMapping(t.session, command.which, command.category, selection);
      return { project: av.removeMapping(project, t.session.id, m.id), message: `Removed “${m.name}”. The music and keyframes are unchanged.`, select: { sessionId: t.session.id, mappingId: null } };
    }
    case 'sync.loop': {
      const t = targetSession(project, selection, false);
      return { project: av.loopBars(project, t.session.id, command.fromBar, command.toBar), message: `Looping bars ${command.fromBar}–${command.toBar} in “${t.session.name}” (sound and picture loop together).`, select: { sessionId: t.session.id } };
    }
    case 'sync.loopOff': {
      const t = targetSession(project, selection, false);
      return { project: av.setLoop(project, t.session.id, { enabled: false }), message: `Loop off in “${t.session.name}”.` };
    }
    case 'sync.marker': {
      const t = targetSession(project, selection, true);
      const total = av.musicTicks(t.project, t.session);
      if (command.tick >= total) throw new OpError(`The music ends at ${formatPosition(total)}.`);
      const r = av.addMarker(t.project, t.session.id, command.tick, command.label || `Marker ${t.session.markers.length + 1}`);
      return { project: r.project, message: `${t.created ? `Created sync session “${t.created}”. ` : ''}Added a manual marker at ${formatPosition(command.tick)}${command.label ? ` (“${command.label}”)` : ''}.`, select: { sessionId: t.session.id } };
    }
    case 'sync.move': {
      const t = targetSession(project, selection, false);
      const name = command.name.toLowerCase();
      const template = NOUNS[name] ?? NOUNS[name.replace(/ (?:cue|motion|animation)$/, '')];
      const cues = template ? t.session.motionCues.filter((c) => c.template === template) : [];
      const markers = t.session.markers.filter((m) => m.label.toLowerCase() === name);
      const total = cues.length + markers.length;
      if (total === 0) throw new OpError(`“${t.session.name}” has no motion cue or marker called “${command.name}”.`);
      if (total > 1) throw new OpError(`“${command.name}” matches ${total} cues or markers. Which one? Select it in the Sync tab and drag it, or rename the markers.`);
      if (cues[0]) {
        const next = av.moveMotionCue(project, t.session.id, cues[0].id, command.tick);
        return { project: next, message: `Moved the ${TEMPLATES[cues[0].template].label.toLowerCase()} cue to ${formatPosition(command.tick)}${cues[0].everyTicks ? ' (its repetitions follow)' : ''}.` };
      }
      return { project: av.moveMarker(project, t.session.id, markers[0]!.id, command.tick), message: `Moved marker “${markers[0]!.label}” to ${formatPosition(command.tick)}.` };
    }
  }
}
